import { useState, useEffect } from 'react';
import type * as Leaflet from 'leaflet';
import { useServiceMap } from '../hooks/useServiceMap';
import { MapPinIcon, PhoneIcon, ChevronDownIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import logoImg from '../assets/optimized/2best-neighbor-home-services-06.webp';
import { useContent } from '../context/ContentContext';

interface CityData {
  name: string;
  lat: number;
  lng: number;
}

interface CountyData {
  name: string;
  color: string;
  cities: CityData[];
}

const counties: CountyData[] = [
  {
    name: 'Collin County',
    color: '#1c252c',
    cities: [
      { name: 'McKinney', lat: 33.1972, lng: -96.6397 },
      { name: 'Allen', lat: 33.1032, lng: -96.6706 },
      { name: 'Frisco', lat: 33.1507, lng: -96.8236 },
      { name: 'Plano', lat: 33.0198, lng: -96.6989 },
      { name: 'Celina', lat: 33.3248, lng: -96.7844 },
      { name: 'Anna', lat: 33.3490, lng: -96.5486 },
      { name: 'Princeton', lat: 33.1801, lng: -96.4981 },
      { name: 'Fairview', lat: 33.1579, lng: -96.6350 },
      { name: 'Lucas', lat: 33.0843, lng: -96.5769 },
      { name: 'Parker', lat: 33.0565, lng: -96.6220 },
      { name: 'Murphy', lat: 33.0151, lng: -96.6131 },
      { name: 'Wylie', lat: 33.0151, lng: -96.5389 },
      { name: 'Richardson', lat: 32.9483, lng: -96.7299 },
      { name: 'Melissa', lat: 33.2860, lng: -96.5728 },
    ],
  },
  {
    name: 'Grayson County',
    color: '#e58c32',
    cities: [
      { name: 'Sherman', lat: 33.6357, lng: -96.6089 },
      { name: 'Denison', lat: 33.7557, lng: -96.5367 },
      { name: 'Whitesboro', lat: 33.6562, lng: -96.9067 },
      { name: 'Van Alstyne', lat: 33.4215, lng: -96.5775 },
      { name: 'Howe', lat: 33.5079, lng: -96.6128 },
      { name: 'Gunter', lat: 33.4479, lng: -96.7475 },
    ],
  },
  {
    name: 'Hunt County',
    color: '#826040',
    cities: [
      { name: 'Greenville', lat: 33.1385, lng: -96.1108 },
      { name: 'Commerce', lat: 33.2468, lng: -95.8999 },
      { name: 'Quinlan', lat: 32.9104, lng: -96.1355 },
      { name: 'Caddo Mills', lat: 33.0676, lng: -96.2286 },
      { name: 'West Tawakoni', lat: 32.8937, lng: -96.0269 },
      { name: 'Campbell', lat: 33.1501, lng: -95.9499 },
      { name: 'Celeste', lat: 33.2918, lng: -96.1922 },
      { name: 'Wolfe City', lat: 33.3707, lng: -96.0686 },
    ],
  },
  {
    name: 'Dallas Area',
    color: '#8b8179',
    cities: [
      { name: 'Dallas', lat: 32.7767, lng: -96.7970 },
      { name: 'Addison', lat: 32.9612, lng: -96.8292 },
      { name: 'Carrollton', lat: 32.9537, lng: -96.8903 },
      { name: 'Farmers Branch', lat: 32.9263, lng: -96.8961 },
      { name: 'Coppell', lat: 32.9546, lng: -97.0150 },
      { name: 'The Colony', lat: 33.0807, lng: -96.8861 },
      { name: 'Little Elm', lat: 33.1626, lng: -96.9375 },
      { name: 'Sachse', lat: 32.9762, lng: -96.5953 },
      { name: 'Mesquite', lat: 32.7668, lng: -96.5992 },
    ],
  },
];

const allCities = counties.flatMap((county) =>
  county.cities.map((city) => ({ ...city, county }))
);


function createMarkerIcon(L: typeof Leaflet, _color: string, isSelected: boolean) {
  const size = isSelected ? 38 : 30;
  const borderColor = isSelected ? '#E8751A' : '#1c252c';
  const borderWidth = isSelected ? 3 : 2;
  const shadow = isSelected ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' : 'drop-shadow(0 1px 3px rgba(0,0,0,0.2))';
  const html = `
    <div style="width:${size}px;height:${size}px;border-radius:50%;background:white;border:${borderWidth}px solid ${borderColor};display:flex;align-items:center;justify-content:center;overflow:hidden;filter:${shadow}">
      <img decoding="async" loading="lazy" src="${logoImg}" alt="" width="32" height="32" style="width:${size - 6}px;height:${size - 6}px;object-fit:contain;" />
    </div>
  `;
  return L.divIcon({
    html,
    className: 'custom-marker-expanded',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

function CountyCard({ county, isExpanded, onToggle, onCityClick, selectedCity }: {
  county: CountyData;
  isExpanded: boolean;
  onToggle: () => void;
  onCityClick: (city: CityData, countyName: string) => void;
  selectedCity: CityData | null;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-brand-orange/30 hover:shadow-sm">
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-between p-5 sm:p-6 text-left"
      >
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${county.color}15` }}
          >
            <MapPinIcon className="w-5 h-5" style={{ color: county.color }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-brand-navy">{county.name}</h3>
            <p className="text-brand-warm-gray text-sm">{county.cities.length} cities served</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline-block text-xs font-semibold uppercase tracking-wider text-brand-orange bg-brand-orange/10 px-3 py-1 rounded-full">
            24/7 Service
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-brand-warm-gray transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      <div hidden={!isExpanded} className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 border-t border-gray-100 pt-4">
          <div className="flex flex-wrap gap-2">
            {county.cities.map((city) => (
              <button
                key={city.name}
                onClick={() => onCityClick(city, county.name)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                  selectedCity?.name === city.name
                    ? 'bg-brand-navy text-white font-medium'
                    : 'bg-brand-off-white/70 text-brand-navy hover:bg-brand-off-white'
                }`}
              >
                <MapPinIcon className={`w-3.5 h-3.5 flex-shrink-0 ${
                  selectedCity?.name === city.name ? 'text-brand-orange' : 'text-brand-warm-gray/60'
                }`} />
                {city.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ServiceAreasExpanded() {
  const [selectedCity, setSelectedCity] = useState<CityData | null>(null);
  const [expandedCounty, setExpandedCounty] = useState<number>(0);
  const { mapRef, mapContainerRef, markersRef, leafletRef, mapReady } = useServiceMap();
  const { content, settings } = useContent();
  const m = content.serviceLocationMap;

  useEffect(() => {
    const map = mapRef.current;
    const markers = markersRef.current;
    const L = leafletRef.current;
    if (!map || !markers || !L) return;

    markers.clearLayers();

    allCities.forEach((city) => {
      const isSelected = selectedCity?.name === city.name;
      const marker = L.marker([city.lat, city.lng], {
        title: `HVAC service in ${city.name}, TX`,
        icon: createMarkerIcon(L, city.county.color, isSelected),
      });

      marker.bindPopup(`
        <div style="text-align:center">
          <p style="font-weight:600;margin:0 0 4px">${city.name}, TX</p>
          <p style="font-size:0.875rem;color:#666;margin:0 0 8px">${city.county.name}</p>
          <a href="tel:${settings.phone.replace(/[^0-9]/g, '')}" style="display:inline-block;padding:12px;background:#E8751A;color:white;font-size:0.875rem;border-radius:4px;text-decoration:none;font-weight:500">Call Now</a>
        </div>
      `);

      marker.on('click', () => {
        setSelectedCity(city);
        map.flyTo([city.lat, city.lng], 14, { duration: 0.8 });
      });

      marker.addTo(markers);
      marker.getElement()?.setAttribute('aria-label', `HVAC service in ${city.name}, TX`);
    });
  }, [selectedCity, settings.phone, mapReady, leafletRef, mapRef, markersRef]);

  const handleCityClick = (city: CityData) => {
    setSelectedCity(city);
    mapRef.current?.flyTo([city.lat, city.lng], 14, { duration: 0.8 });

    const mapEl = document.getElementById('expanded-map');
    if (mapEl) {
      mapEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleToggleCounty = (index: number) => {
    setExpandedCounty(expandedCounty === index ? -1 : index);
  };

  return (
    <section id="service-areas" className="bg-white relative overflow-hidden">
      <div className="bg-brand-off-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
            {counties.map((county) => (
              <div
                key={county.name}
                className="bg-white rounded-xl p-5 sm:p-6 border border-gray-100 hover:border-brand-orange/20 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: county.color }}
                  />
                  <span className="text-sm font-semibold text-brand-navy truncate">
                    {county.name}
                  </span>
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-brand-navy mb-1">{county.cities.length}</p>
                <p className="text-brand-warm-gray text-sm">Cities Served</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-6 lg:gap-10">
            <div className="lg:col-span-5 xl:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-3">
                <div className="bg-white rounded-xl border border-gray-100 p-5 sm:p-6 mb-4">
                  <h2 className="!leading-[1.4] text-xl font-serif font-bold text-brand-navy mb-2">{m.directoryHeading}</h2>
                  <p className="text-brand-warm-gray text-sm leading-relaxed">
                    {m.directoryDescription}
                  </p>
                </div>

                {counties.map((county, index) => (
                  <CountyCard
                    key={county.name}
                    county={county}
                    isExpanded={expandedCounty === index}
                    onToggle={() => handleToggleCounty(index)}
                    onCityClick={handleCityClick}
                    selectedCity={selectedCity}
                  />
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 xl:col-span-8">
              <div id="expanded-map" className="relative rounded-2xl shadow-sm overflow-hidden border border-gray-200 h-[350px] sm:h-[500px] lg:h-[650px]">
                <div ref={mapContainerRef} style={{ height: '100%', width: '100%', position: 'relative', zIndex: 0 }} />

                {selectedCity && (
                  <div className="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-72 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-[1000] animate-fade-in">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 bg-brand-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPinIcon className="w-4 h-4 text-brand-orange" />
                      </div>
                      <div>
                        <p className="font-bold text-brand-navy">{selectedCity.name}, TX</p>
                        <p className="text-brand-warm-gray text-xs">Full HVAC services available</p>
                      </div>
                    </div>
                    <div className="space-y-1.5 mb-3">
                      {m.services.map((svc) => (
                        <div key={svc} className="flex items-center gap-2">
                          <CheckCircleIcon className="w-3.5 h-3.5 text-brand-orange flex-shrink-0" />
                          <span className="text-xs text-brand-navy/70">{svc}</span>
                        </div>
                      ))}
                    </div>
                    <a
                      href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                      className="flex items-center justify-center gap-2 w-full bg-brand-navy text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-brand-orange transition-colors text-sm"
                    >
                      <PhoneIcon className="w-4 h-4" />
                      Schedule Service
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center gap-4 flex-wrap">
                {counties.map((county) => (
                  <div key={county.name} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: county.color }} />
                    <span className="text-xs text-brand-warm-gray font-medium">{county.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="bg-brand-navy rounded-2xl p-6 sm:p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" />

          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5 mb-4">
                <MapPinIcon className="w-4 h-4 text-brand-orange" />
                <span className="text-white/80 text-sm font-medium">{m.ctaBadge}</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-3">
                {m.ctaHeading}
              </h3>
              <p className="text-white/70 leading-relaxed">
                {m.ctaDescription}
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
              <a
                href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center justify-center gap-3 w-full bg-brand-orange text-white px-8 py-4 rounded-xl font-semibold hover:bg-brand-tan transition-colors duration-300 group"
              >
                <PhoneIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>{settings.phone}</span>
              </a>
              <a
                href={settings.bookingUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 w-full bg-white/10 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors duration-300 border border-white/20"
              >
                {m.ctaCallbackLabel}
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-marker-expanded {
          background: transparent;
          border: none;
        }
        #service-areas .leaflet-top,
        #service-areas .leaflet-bottom {
          z-index: 400 !important;
        }
        #service-areas .leaflet-pane {
          z-index: 300 !important;
        }
        #service-areas .leaflet-map-pane {
          z-index: 200 !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
        }
        .leaflet-popup-content {
          margin: 12px 16px;
        }
      `}</style>
    </section>
  );
}
